// useListKeyboard — ZDIEĽANÁ klávesová navigácia zoznamu (ústava 54.2, 56.5)
// ════════════════════════════════════════════════════════════════════
// Jediný zdroj pravdy pre šípky v master-detail zoznamoch. Zmena tu = platí
// vo VŠETKÝCH moduloch. Povinné v _ModuleTemplate.jsx → nový modul ho zdedí.
//
//   ↑ / ↓           prev / next položka
//   PageUp/PageDown  skok o 10
//   Home / End       prvá / posledná
//   Enter            otvoriť vybranú (onOpen)
//
// Vybraná položka sa po pohybe automaticky odscrolluje do view (54.6 — kurzor
// musí byť vždy viditeľný). Ignoruje sa, keď je fokus v inpute/textarea/selecte.
//
// POUŽITIE:
//   useListKeyboard({
//     items:      filtered,                  // pole záznamov, každý má .id
//     selectedId,                            // aktuálne vybrané id
//     onSelect:   (id) => setSelectedId(id),  // zmena výberu
//     onOpen:     (item) => openEdit(item),   // Enter na vybranej
//     enabled:    true,                       // voliteľne vypnúť (napr. modal)
//     dataAttr:   'data-row-id',              // atribút riadku pre scroll-into-view
//   })

import { useEffect } from 'react'

export function useListKeyboard({
  items,
  selectedId,
  onSelect,
  onOpen,
  enabled = true,
  dataAttr = 'data-row-id',
}) {
  useEffect(() => {
    if (!enabled || !items || items.length === 0) return

    const onKey = (e) => {
      // neprekáž písaniu vo formulárových poliach
      const t = e.target
      const tag = t && t.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return
      // 16. 9. 2026 (Kronika 511, ústava 54.1 a 54.4): keď je otvorené okno (aria-modal) a zoznam nie je v ňom, klávesy patria oknu —
      // zoznam pod oknom sa nehýbe a Esc nezruší výber
      const dialogy = document.querySelectorAll('[aria-modal="true"]')
      if (dialogy.length) {
        const riadok = document.querySelector('[' + dataAttr + ']')
        if (!riadok || !dialogy[dialogy.length - 1].contains(riadok)) return
      }

      const idx = items.findIndex(x => x.id === selectedId)

      const pick = (i) => {
        const clamped = Math.max(0, Math.min(items.length - 1, i))
        const item = items[clamped]
        if (!item) return
        onSelect(item.id)
        // kurzor musí ostať viditeľný (ústava 54.6)
        requestAnimationFrame(() => {
          const el = document.querySelector(`[${dataAttr}="${item.id}"]`)
          if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
        })
      }

      switch (e.key) {
        case 'ArrowDown':  e.preventDefault(); pick((idx < 0 ? -1 : idx) + 1); break
        case 'ArrowUp':    e.preventDefault(); pick((idx < 0 ? 0 : idx) - 1); break
        case 'PageDown':   e.preventDefault(); pick((idx < 0 ? 0 : idx) + 10); break
        case 'PageUp':     e.preventDefault(); pick((idx < 0 ? 0 : idx) - 10); break
        case 'Home':       e.preventDefault(); pick(0); break
        case 'End':        e.preventDefault(); pick(items.length - 1); break
        case 'Enter':
          if (idx >= 0 && typeof onOpen === 'function') { e.preventDefault(); onOpen(items[idx]) }
          break
        case 'Escape':
          // ustava 54.4: Esc = zrusit vyber (modaly maju vlastny Esc; hook je enabled=false ked su otvorene)
          if (selectedId != null) { e.preventDefault(); onSelect(null) }
          break
        default: break
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [items, selectedId, onSelect, onOpen, enabled, dataAttr])
}

export default useListKeyboard
